import type { Resource } from '../types';

export type SearchMode = 'normal' | 'smart';

type SearchScore = {
  score: number;
  reasons: string[];
};

export interface RankedSmartSearchResult {
  resource: Resource;
  score: number;
  reasons: string[];
}

const MIN_TOKEN_LENGTH = 2;

const QUERY_SYNONYMS: Record<string, string[]> = {
  ai: ['artificial intelligence', 'aigc'],
  llm: ['large language model', 'language model'],
  agent: ['assistant', 'autonomous agent', 'multi agent', 'multi-agent'],
  multiagent: ['multi-agent', 'agent orchestration', 'multi agent'],
  rag: ['retrieval augmented generation', 'retrieval'],
  workflow: ['automation', 'orchestration', 'pipeline'],
  搜索: ['search', 'retrieval'],
  智能体: ['agent', 'assistant', 'multi-agent'],
  多智能体: ['multi-agent', 'agent orchestration', 'multi agent'],
  自动化: ['automation', 'workflow', 'orchestration'],
  工单: ['ticket', 'support', 'helpdesk'],
  客服: ['customer support', 'support', 'service desk'],
  向量: ['vector', 'embedding'],
  嵌入: ['embedding', 'vector'],
  框架: ['framework', 'sdk', 'library'],
};

function normalizeText(input: string): string {
  return input
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^\w\u4e00-\u9fff\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeToken(token: string): string {
  return normalizeText(token).replace(/\s+/g, '');
}

function extractTokens(query: string): string[] {
  const normalized = normalizeText(query);
  if (!normalized) return [];
  const parts = normalized.split(' ').map(item => item.trim()).filter(Boolean);
  const tokenSet = new Set<string>();
  for (const part of parts) {
    if (part.length < MIN_TOKEN_LENGTH) continue;
    tokenSet.add(normalizeToken(part));
  }
  return Array.from(tokenSet);
}

function expandTokens(tokens: string[]): string[] {
  const expanded = new Set<string>(tokens);
  for (const token of tokens) {
    const synonyms = QUERY_SYNONYMS[token] || [];
    for (const synonym of synonyms) {
      const synonymTokens = extractTokens(synonym);
      for (const item of synonymTokens) {
        expanded.add(item);
      }
    }
  }
  return Array.from(expanded);
}

function tokenizeResource(resource: Resource): { title: string; description: string; tags: string[] } {
  const title = normalizeText(`${resource.title || ''} ${resource.title_zh || ''}`);
  const description = normalizeText(`${resource.description || ''} ${resource.description_zh || ''}`);
  const tags = (resource.tags || []).map(tag => normalizeText(tag)).filter(Boolean);
  return { title, description, tags };
}

function containsToken(text: string, token: string): boolean {
  if (!text || !token) return false;
  if (text.includes(token)) return true;

  // Soft match for longer tokens to improve typo tolerance.
  if (token.length >= 5) {
    return text.includes(token.slice(0, 4));
  }
  return false;
}

function scoreResource(resource: Resource, query: string): SearchScore {
  const normalizedQuery = normalizeText(query);
  const queryTokens = extractTokens(normalizedQuery);
  if (queryTokens.length === 0) {
    return { score: 0, reasons: [] };
  }

  const expandedTokens = expandTokens(queryTokens);
  const { title, description, tags } = tokenizeResource(resource);
  const tagBlob = tags.join(' ');
  const reasons: string[] = [];
  let score = 0;

  if (normalizedQuery.length >= MIN_TOKEN_LENGTH && title.includes(normalizedQuery)) {
    score += 8;
    reasons.push('title_phrase');
  }

  if (normalizedQuery.length >= MIN_TOKEN_LENGTH && description.includes(normalizedQuery)) {
    score += 3;
    reasons.push('description_phrase');
  }

  let matchedRawTokenCount = 0;

  for (const token of expandedTokens) {
    let matched = false;
    if (containsToken(title, token)) {
      score += 4;
      reasons.push(`title:${token}`);
      matched = true;
    }
    if (containsToken(tagBlob, token)) {
      score += 3;
      reasons.push(`tag:${token}`);
      matched = true;
    }
    if (containsToken(description, token)) {
      score += 2;
      reasons.push(`description:${token}`);
      matched = true;
    }
    if (!matched && token.length >= 6) {
      const fuzzy = token.slice(0, 5);
      if (containsToken(`${title} ${description}`, fuzzy)) {
        score += 1;
        reasons.push(`fuzzy:${token}`);
      }
    }
  }

  for (const token of queryTokens) {
    if (containsToken(title, token) || containsToken(description, token) || containsToken(tagBlob, token)) {
      matchedRawTokenCount += 1;
    }
  }

  if (matchedRawTokenCount === queryTokens.length) {
    score += 4;
    reasons.push('all_tokens_matched');
  }

  return { score, reasons: Array.from(new Set(reasons)).slice(0, 8) };
}

export function rankResourcesBySmartSearch(resources: Resource[], query: string): RankedSmartSearchResult[] {
  const trimmed = query.trim();
  if (!trimmed) {
    return resources.map(resource => ({ resource, score: 0, reasons: [] }));
  }

  return resources
    .map((resource) => {
      const result = scoreResource(resource, trimmed);
      return {
        resource,
        score: result.score,
        reasons: result.reasons,
      };
    })
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || a.resource.title.localeCompare(b.resource.title));
}
